import { useState, useRef, useEffect } from 'react'

export default function PullToRefresh({ onRefresh, children, threshold = 60 }) {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [pullDistance, setPullDistance] = useState(0)
  const [startY, setStartY] = useState(0)
  const containerRef = useRef(null)

  const handleTouchStart = (e) => {
    if (window.scrollY === 0) {
      setStartY(e.touches[0].clientY)
    }
  }

  const handleTouchMove = (e) => {
    if (window.scrollY === 0 && startY > 0) {
      const currentY = e.touches[0].clientY
      const distance = Math.max(0, currentY - startY)
      setPullDistance(Math.min(distance, threshold * 1.5))
    }
  }

  const handleTouchEnd = async () => {
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true)
      try {
        await onRefresh()
      } catch (error) {
        console.error('Refresh failed:', error)
      } finally {
        setIsRefreshing(false)
      }
    }
    setPullDistance(0)
    setStartY(0)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: true })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [pullDistance, startY, threshold, isRefreshing])

  const pullProgress = Math.min(pullDistance / threshold, 1)
  const shouldTrigger = pullDistance >= threshold

  return (
    <div ref={containerRef} className="relative">
      {/* Pull to Refresh Indicator */}
      <div 
        className="absolute top-0 left-0 right-0 flex items-center justify-center transition-transform duration-200 ease-out z-10"
        style={{ 
          transform: `translateY(${Math.max(0, pullDistance - 40)}px)`,
          opacity: pullDistance > 10 ? 1 : 0
        }}
      >
        <div className="bg-white rounded-full shadow-lg p-3 flex items-center gap-2">
          <div 
            className={`w-5 h-5 border-2 border-slate-300 rounded-full transition-all duration-200 ${
              isRefreshing 
                ? 'border-t-slate-900 animate-spin' 
                : shouldTrigger 
                ? 'border-t-slate-900' 
                : ''
            }`}
            style={{
              transform: `rotate(${pullProgress * 180}deg)`
            }}
          />
          <span className="text-sm font-medium text-slate-700">
            {isRefreshing 
              ? 'Refreshing...' 
              : shouldTrigger 
              ? 'Release to refresh' 
              : 'Pull to refresh'
            }
          </span>
        </div>
      </div>

      {/* Content */}
      <div 
        className="transition-transform duration-200 ease-out"
        style={{ 
          transform: `translateY(${pullDistance * 0.5}px)` 
        }}
      >
        {children}
      </div>
    </div>
  )
}
